import type {
    ICampaignCommissionConfig,
    ICampaignCommissionEstimate,
    ICampaignCommissionExternal,
} from '../redux/buzzbridge/types';

/** Default marketing pool when display_pool_cents omitted: rate% of $100 → cents. */
export function defaultContestDisplayPoolCents(rate: number): number {
    const r = Number.isFinite(rate) ? Math.max(0, rate) : 0;
    return Math.round(r * 100 * 100);
}

/** Default external pool: $1,500. */
export const DEFAULT_EXTERNAL_DISPLAY_POOL_CENTS = 150_000;

export function resolveCommissionDisplayPoolCents(
    commission?: Pick<ICampaignCommissionConfig, 'target_type' | 'rate' | 'display_pool_cents'> | null,
): number {
    if (commission?.display_pool_cents != null && commission.display_pool_cents > 0) {
        return Math.round(commission.display_pool_cents);
    }
    if (commission?.target_type === 'external') return DEFAULT_EXTERNAL_DISPLAY_POOL_CENTS;
    return defaultContestDisplayPoolCents(Number(commission?.rate) || 0);
}

/** Seed total_budget (major units) from display pool so create validation passes. */
export function commissionBudgetMajorUnits(commission?: ICampaignCommissionConfig | null): number {
    return resolveCommissionDisplayPoolCents(commission) / 100;
}

export function emptyCommissionConfig(target: 'contest' | 'external' = 'contest'): ICampaignCommissionConfig {
    if (target === 'external') {
        return {
            target_type: 'external',
            rate: 15,
            requires_approval: true,
            display_pool_cents: DEFAULT_EXTERNAL_DISPLAY_POOL_CENTS,
            external: {
                product_name: '',
                product_description: '',
                product_url: '',
                fulfillment: 'physical',
            },
        };
    }
    return {
        target_type: 'contest',
        rate: 20,
        requires_approval: true,
        contest: '',
        display_pool_cents: defaultContestDisplayPoolCents(20),
    };
}

export function formatCommissionPoolDollars(cents: number): string {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** Footer summary for commission create step: per-seat × seats → pool total (contest), or flat estimate (external). */
export function commissionWizardFooterSummary(
    commission: Pick<ICampaignCommissionConfig, 'target_type' | 'rate' | 'display_pool_cents'> | null | undefined,
    seats: number,
): { formulaLabel: string; totalLabel: string; totalValue: string } | null {
    const poolCents = resolveCommissionDisplayPoolCents(commission);
    if (poolCents <= 0) return null;
    const slots = Math.max(1, Math.round(Number(seats) || 1));

    if (commission?.target_type === 'external') {
        return {
            formulaLabel: `${slots} Creator seat${slots === 1 ? '' : 's'}`,
            totalLabel: 'Est. per creator',
            totalValue: formatCommissionPoolDollars(poolCents),
        };
    }

    const perSeatCents = Math.round(poolCents / slots);
    return {
        formulaLabel: `${formatCommissionPoolDollars(perSeatCents)} × ${slots} creators`,
        totalLabel: 'Possible total',
        totalValue: formatCommissionPoolDollars(poolCents),
    };
}

export function commissionEstimateSummary(estimate?: ICampaignCommissionEstimate | null): string | null {
    if (!estimate) return null;
    const earning =
        estimate.possible_earning != null
            ? formatCommissionPoolDollars(Math.round(Number(estimate.possible_earning) * 100))
            : estimate.possible_earning_cents != null
            ? formatCommissionPoolDollars(estimate.possible_earning_cents)
            : null;
    if (!earning) return null;
    if (estimate.shrinks_with_fill) {
        return `Up to ${earning} per open seat · ${estimate.commission_rate ?? ''}% of attributed fees`;
    }
    return `Est. ${earning} · ${estimate.commission_rate ?? ''}% commission`;
}

export function sanitizeCommissionForPayload(commission: ICampaignCommissionConfig): ICampaignCommissionConfig | null {
    const rate = Number(commission.rate);
    if (!Number.isFinite(rate) || rate <= 0) return null;

    const display_pool_cents = resolveCommissionDisplayPoolCents(commission);

    if (commission.target_type === 'external') {
        const external: ICampaignCommissionExternal = {
            ...(commission.external || {}),
            product_name: String(commission.external?.product_name || '').trim(),
            product_url: String(commission.external?.product_url || '').trim() || undefined,
        };
        if (!external.product_name) return null;
        return {
            target_type: 'external',
            rate,
            requires_approval: commission.requires_approval !== false,
            display_pool_cents,
            external,
        };
    }

    const contestId = String(
        typeof commission.contest === 'object' && commission.contest
            ? (commission.contest as { _id?: string })._id || ''
            : commission.contest || '',
    ).trim();
    if (!contestId) return null;
    return {
        target_type: 'contest',
        rate,
        requires_approval: commission.requires_approval !== false,
        contest: contestId,
        display_pool_cents,
    };
}
